/**
 * スキーママイグレーション関数
 *
 * 各バージョン間の変換ロジックを定義する。
 * マイグレーションは順次適用される（v1 → v2 → v3 → ...）。
 *
 * 新しいマイグレーションを追加する際は:
 * 1. types.ts に新しい ExportDataVx 型を追加
 * 2. このファイルに migrateVxToVy 関数を追加
 * 3. MIGRATIONS オブジェクトに登録
 */

import type {
  ExportDataV1,
  // ExportDataV2,
  AnyVersionExportData,
  LatestExportData,
} from './types';
import { CURRENT_VERSION } from './types';

/**
 * マイグレーション関数の型
 */
type MigrationFn<TFrom, TTo> = (data: TFrom) => TTo;

/**
 * バージョンごとのマイグレーション関数マップ
 * キー: 変換元バージョン
 * 値: 次のバージョンへの変換関数
 */
const MIGRATIONS: Record<number, MigrationFn<AnyVersionExportData, AnyVersionExportData>> = {
  // 例: v1 → v2 のマイグレーション
  // 1: migrateV1ToV2,
};

// マイグレーション関数の実装例（将来用）:
//
// function migrateV1ToV2(data: ExportDataV1): ExportDataV2 {
//   return {
//     ...data,
//     version: 2,
//     // 新しいフィールドの追加
//     newField: 'default value',
//     // フィールドの変換
//     renamedField: data.oldField,
//   };
// }

/**
 * エクスポートデータを最新バージョンにマイグレーション
 *
 * @param data 任意のバージョンのエクスポートデータ
 * @returns 最新バージョンのエクスポートデータ
 * @throws マイグレーションに失敗した場合
 */
export function migrateToLatest(data: AnyVersionExportData): LatestExportData {
  let current: AnyVersionExportData = data;

  // 現在のバージョンが最新になるまで順次マイグレーション
  while (current.version < CURRENT_VERSION) {
    const migrate = MIGRATIONS[current.version];

    if (!migrate) {
      throw new Error(
        `マイグレーション関数が見つかりません: v${current.version} → v${current.version + 1}`
      );
    }

    current = migrate(current);
  }

  return current as LatestExportData;
}

/**
 * データが既知のバージョンかどうかを判定
 *
 * @param data 検証対象のデータ
 * @returns 既知のバージョンの場合 true
 */
export function isKnownVersion(data: unknown): data is AnyVersionExportData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const obj = data as Record<string, unknown>;

  if (typeof obj.version !== 'number') {
    return false;
  }

  // バージョン1以上かつ現在のバージョン以下であれば既知
  return obj.version >= 1 && obj.version <= CURRENT_VERSION;
}

/**
 * マイグレーションが必要かどうかを判定
 *
 * @param data エクスポートデータ
 * @returns マイグレーションが必要な場合 true
 */
export function needsMigration(data: AnyVersionExportData): boolean {
  return data.version < CURRENT_VERSION;
}
